import type { Media } from "@atrium/shared";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code: string,
    public fields?: Record<string, string>,
  ) {
    super(message);
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function request<T>(method: Method, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      signal,
      headers: {
        "X-Requested-With": "atrium",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "Can't reach Atrium. Check your connection.", "network");
  }
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const d = (data ?? {}) as { message?: string; error?: string; fields?: Record<string, string> };
    if (res.status === 401) window.dispatchEvent(new CustomEvent("atrium:unauthorized"));
    throw new ApiError(res.status, d.message ?? "Something went wrong", d.error ?? "error", d.fields);
  }
  return data as T;
}

function safeJson(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>("GET", path, undefined, signal),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown = {}) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown = {}) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

export function qs(params: Record<string, string | number | boolean | null | undefined>) {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : "";
}

export type UploadPurpose = "attachment" | "avatar" | "banner" | "community_icon" | "community_banner";

/** Upload a file with progress reporting. Validation happens server-side. */
export function uploadFile(file: File, purpose: UploadPurpose, onProgress?: (fraction: number) => void, signal?: AbortSignal): Promise<Media> {
  return uploadTo(`/api/media?purpose=${purpose}`, file, file.name, onProgress, signal);
}

/** POST one file as multipart form data to an upload endpoint, with progress. */
export function uploadTo(url: string, file: Blob, filename: string, onProgress?: (fraction: number) => void, signal?: AbortSignal): Promise<Media> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.setRequestHeader("X-Requested-With", "atrium");
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      const data = safeJson(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as Media);
      else reject(new ApiError(xhr.status, data?.message ?? "Upload failed", data?.error ?? "error"));
    };
    xhr.onerror = () => reject(new ApiError(0, "Upload failed. Check your connection.", "network"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    signal?.addEventListener("abort", () => xhr.abort());
    const form = new FormData();
    form.append("file", file, filename);
    xhr.send(form);
  });
}

export function errorMessage(e: unknown, fallback = "Something went wrong"): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error && e.name !== "AbortError") return e.message || fallback;
  return fallback;
}
